import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useMarketCoins, MAX_MARKET_PAGES } from '../../hooks/useMarketCoins';
import { useCurrency } from '../../context/CurrencyContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatPercent } from '../../utils/formatters';
import { Search, RefreshCw, AlertCircle, TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Sparkline ───────────────────────────────────────────────────────────────
// `width`/`height` default to the desktop size (100×36) so the table is unchanged;
// the mobile row passes smaller dims.
function Sparkline({ data, positive, width = 100, height = 36 }: { data: number[]; positive: boolean; width?: number; height?: number }) {
  if (data.length < 2) return <div style={{ width, height }} />;

  // Thin to ≤60 points for rendering
  const pts = data.length > 60
    ? data.filter((_, i) => i % Math.ceil(data.length / 60) === 0 || i === data.length - 1)
    : data;

  const w = width, h = height, pad = 2;
  const min = pts.reduce((m, v) => (v < m ? v : m), Infinity);
  const max = pts.reduce((m, v) => (v > m ? v : m), -Infinity);
  const range = max - min || 1;

  const points = pts.map((v, i) => {
    const x = pad + (i / (pts.length - 1)) * (w - 2 * pad);
    const y = pad + ((max - v) / range) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  const color = positive ? '#10b981' : '#ef4444';

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Compact number formatter ─────────────────────────────────────────────────
function compact(n: number): string {
  if (!isFinite(n) || isNaN(n) || n === 0) return '—';
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9)  return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6)  return `$${(n / 1e6).toFixed(2)}M`;
  return `$${n.toLocaleString('en-US')}`;
}

// ─── Change cell ─────────────────────────────────────────────────────────────
function ChangeCell({ value }: { value: number }) {
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-sm font-medium ${up ? 'text-emerald-400' : 'text-red-400'}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {formatPercent(value)}
    </span>
  );
}

// ─── Build synthetic holding + coinInfo for the modal ────────────────────────
// ─── Main component ───────────────────────────────────────────────────────────
export function MarketTable() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Page lives in the URL (?mktPage=N) — same pattern as ?tab= — so it survives
  // opening a coin and pressing Back. Missing / invalid / out-of-range → page 1.
  const rawPage = Number(searchParams.get('mktPage'));
  const page = Number.isInteger(rawPage) && rawPage >= 1 && rawPage <= MAX_MARKET_PAGES ? rawPage : 1;

  const setPage = (next: number) => {
    const clamped = Math.min(Math.max(1, next), MAX_MARKET_PAGES);
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      if (clamped === 1) n.delete('mktPage'); // keep ?tab=market clean on page 1
      else n.set('mktPage', String(clamped));
      return n;
    });
  };

  const { coins, loading, error, lastUpdated, refresh } = useMarketCoins(page);
  const { fmt } = useCurrency();
  const [query, setQuery] = useState('');

  // Scroll back to the top of the market view when the page changes (skip mount).
  const topRef   = useRef<HTMLDivElement>(null);
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page]);

  // Return search for coin links so Back lands on the right market page.
  const returnSearch = page > 1 ? `?tab=market&mktPage=${page}` : '?tab=market';

  // Global rank range for the current page (100 per page), for the footer line.
  const rangeStart = (page - 1) * 100 + 1;
  const rangeEnd   = (page - 1) * 100 + coins.length;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return coins;
    return coins.filter(
      (c) => c.name.toLowerCase().includes(q) || c.symbol.toLowerCase().includes(q)
    );
  }, [coins, query]);

  const timeStr = lastUpdated
    ? lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <>
      <div ref={topRef} className="space-y-5">
        {/* ── Toolbar ── */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          {/* Search */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or symbol…"
              className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-9 pr-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-slate-500 transition-colors"
            />
          </div>

          {/* Status + refresh */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {timeStr && (
              <span className="text-xs text-slate-500">
                Updated {timeStr}
                {error && <span className="text-yellow-500 ml-1">·</span>}
              </span>
            )}
            <button
              onClick={refresh}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-4 py-2.5">
            <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
            <p className="text-xs text-yellow-400">{error}</p>
          </div>
        )}

        {/* ── Table (desktop ≥ md) / list (mobile < md) ── */}
        {loading && coins.length === 0 ? (
          <div className="border-t border-slate-700 flex flex-col items-center justify-center py-20 gap-3">
            <RefreshCw className="w-6 h-6 text-white animate-spin" />
            <span className="text-sm text-slate-500">Loading market data…</span>
          </div>
        ) : (
          <>
            {/* Desktop table — unchanged */}
            <div className="hidden md:block overflow-x-auto border-t border-slate-700">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="px-4 py-3.5 text-left label-caps text-slate-500 w-12 pl-0">#</th>
                    <th className="px-4 py-3.5 text-left label-caps text-slate-500">Coin</th>
                    <th className="px-4 py-3.5 text-right label-caps text-slate-500">Price</th>
                    <th className="px-4 py-3.5 text-right label-caps text-slate-500">24h</th>
                    <th className="px-4 py-3.5 text-right label-caps text-slate-500">7d</th>
                    <th className="px-4 py-3.5 text-right label-caps text-slate-500">Market Cap</th>
                    <th className="px-4 py-3.5 text-right label-caps text-slate-500">Volume (24h)</th>
                    <th className="px-4 py-3.5 text-right label-caps text-slate-500 pr-0">7d Chart</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/60">
                  {filtered.map((coin) => (
                    <tr
                      key={coin.id}
                      onClick={() => navigate(`/coin/${coin.id}`, { state: { from: 'market', fromSearch: returnSearch } })}
                      className="hover:bg-slate-800/30 transition-colors cursor-pointer group"
                    >
                      {/* Rank */}
                      <td className="px-5 py-5 pl-0">
                        <span className="text-[14px] text-slate-500">{coin.marketCapRank}</span>
                      </td>

                      {/* Coin */}
                      <td className="px-5 py-5">
                        <div className="flex items-center gap-3.5">
                          <img
                            src={coin.image}
                            alt={coin.name}
                            className="w-9 h-9 rounded-full flex-shrink-0"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                          <div>
                            <p className="text-[14px] font-medium text-slate-100 group-hover:text-slate-100 transition-colors">
                              {coin.name}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">{coin.symbol}</p>
                          </div>
                        </div>
                      </td>

                      {/* Price */}
                      <td className="px-5 py-5 text-right">
                        <span className="text-[14px] font-medium text-slate-200">{fmt(coin.currentPrice)}</span>
                      </td>

                      {/* 24h */}
                      <td className="px-5 py-5 text-right">
                        <ChangeCell value={coin.change24h} />
                      </td>

                      {/* 7d */}
                      <td className="px-5 py-5 text-right">
                        <ChangeCell value={coin.change7d} />
                      </td>

                      {/* Market Cap */}
                      <td className="px-5 py-5 text-right">
                        <span className="text-[14px] text-slate-300">{compact(coin.marketCap)}</span>
                      </td>

                      {/* Volume */}
                      <td className="px-5 py-5 text-right">
                        <span className="text-[14px] text-slate-400">{compact(coin.volume24h)}</span>
                      </td>

                      {/* Sparkline */}
                      <td className="px-5 py-5 pr-0 text-right">
                        <div className="flex justify-end">
                          <Sparkline data={coin.sparkline7d} positive={coin.change7d >= 0} />
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filtered.length === 0 && !loading && (
                    <tr>
                      <td colSpan={8} className="py-16 text-center">
                        <p className="text-slate-500 text-sm">No coins match "{query}"</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile list — compact CMC-style rows */}
            <div className="md:hidden border-t border-slate-700">
              {filtered.length === 0 ? (
                <p className="py-16 text-center text-slate-500 text-sm">No coins match "{query}"</p>
              ) : (
                <div className="divide-y divide-slate-700/40">
                  {filtered.map((coin) => (
                    <button
                      key={coin.id}
                      type="button"
                      onClick={() => navigate(`/coin/${coin.id}`, { state: { from: 'market', fromSearch: returnSearch } })}
                      className="w-full flex items-center gap-3 py-3 text-left transition-colors active:bg-slate-800/30"
                    >
                      {/* Rank */}
                      <span className="w-5 flex-shrink-0 text-right text-[12px] text-slate-500 tabular-nums">
                        {coin.marketCapRank}
                      </span>

                      {/* Icon */}
                      <img
                        src={coin.image}
                        alt={coin.name}
                        className="w-8 h-8 rounded-full flex-shrink-0"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />

                      {/* Name / symbol */}
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-medium text-slate-100 truncate">{coin.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{coin.symbol}</p>
                      </div>

                      {/* Sparkline */}
                      <div className="flex-shrink-0">
                        <Sparkline data={coin.sparkline7d} positive={coin.change7d >= 0} width={60} height={28} />
                      </div>

                      {/* Price + 24h */}
                      <div className="flex-shrink-0 text-right">
                        <p className="text-[14px] font-semibold text-slate-100">{fmt(coin.currentPrice)}</p>
                        <div className="mt-0.5 flex justify-end">
                          <ChangeCell value={coin.change24h} />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Search matched nothing on this page — hint to try another (search is per-page) */}
        {query.trim() && filtered.length === 0 && !loading && coins.length > 0 && (
          <p className="text-xs text-slate-600 text-center">
            No matches on page {page}. Try another page.
          </p>
        )}

        {/* Pagination — 100 coins per page, pages 1–5 (top 500) */}
        <div className="flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Previous
          </button>
          <span className="text-xs text-slate-500 tabular-nums">Page {page} of {MAX_MARKET_PAGES}</span>
          <button
            type="button"
            onClick={() => setPage(page + 1)}
            disabled={page >= MAX_MARKET_PAGES}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {!loading && coins.length > 0 && (
          <p className="text-xs text-slate-600 text-center">
            Showing coins {rangeStart}–{rangeEnd} of top {MAX_MARKET_PAGES * 100} · Auto-refreshes every 60s · Data from CoinGecko
          </p>
        )}
      </div>

    </>
  );
}
